import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PricingData {
  id?: number;
  service_provider: string;
  operation_type: string;
  model: string;
  input_cost_per_1m_tokens: number | null;
  output_cost_per_1m_tokens: number | null;
  cost_per_operation: number | null;
  avg_credit_cost: number | null;
  created_at: string;
  updated_at: string;
}

export interface TransformedPricingData {
  creditsPerUse: number;
  displayName: string;
  serviceProvider: string;
  model: string;
  operation_type: string;
}

// Hook to fetch live pricing data from database
export const usePricingData = () => {
  return useQuery({
    queryKey: ['pricing-data'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_usage_pricing')
        .select('*')
        .order('operation_type');
      
      if (error) throw error;
      return data as PricingData[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (replaces cacheTime)
  });
};

// Map operation types to display names
const getDisplayName = (operationType: string): string => {
  const displayMap: Record<string, string> = {
    'research': 'Research Articles',
    'image_generation': 'Images',
    'text_generation': 'Pages of Text', 
    'threaded_generation': 'Recommended Tasks',
    'agent_chat': 'Chat Messages',
  };
  return displayMap[operationType] || operationType;
};

// Transform pricing data for frontend use
export const transformPricingData = (pricingData: PricingData[]): Record<string, TransformedPricingData> => {
  return pricingData.reduce((acc, item) => {
    acc[item.operation_type] = {
      creditsPerUse: Number(item.avg_credit_cost || 0),
      displayName: getDisplayName(item.operation_type),
      serviceProvider: item.service_provider,
      model: item.model,
      operation_type: item.operation_type,
    };
    return acc;
  }, {} as Record<string, TransformedPricingData>);
};

// Calculate usage breakdown dynamically based on live pricing
export const calculateDynamicUsage = (totalCredits: number, pricingData: PricingData[]) => {
  // Filter to only show the 4 desired operation types
  const allowedOperations = ['research', 'image_generation', 'text_generation', 'threaded_generation'];
  
  return pricingData
    .filter(item => allowedOperations.includes(item.operation_type))
    .map(item => {
      const creditsPerUse = Number(item.avg_credit_cost || 0);
      const totalUses = creditsPerUse > 0 ? Math.floor(totalCredits / creditsPerUse) : 0;
      const displayName = getDisplayName(item.operation_type);
      
      return {
        operation: item.operation_type,
        displayName,
        creditsPerUse,
        totalUses,
        displayText: `+${totalUses} ${displayName}`,
        serviceProvider: item.service_provider,
        model: item.model,
      };
    });
};

// Get specific pricing for an operation type
export const getPricingForOperation = (pricingData: PricingData[], operationType: string): TransformedPricingData | null => {
  const item = pricingData.find(p => p.operation_type === operationType);
  if (!item) return null;
  
  return {
    creditsPerUse: Number(item.avg_credit_cost || 0),
    displayName: getDisplayName(item.operation_type),
    serviceProvider: item.service_provider,
    model: item.model,
    operation_type: item.operation_type,
  };
};

// Calculate total uses for all features (replacement for old calculateTotalUses)
export const calculateTotalUsesFromPricing = (credits: number, pricingData: PricingData[]): Record<string, number> => {
  // Filter to only show the 4 desired operation types
  const allowedOperations = ['research', 'image_generation', 'text_generation', 'threaded_generation'];
  
  return pricingData
    .filter(item => allowedOperations.includes(item.operation_type))
    .reduce((acc, item) => {
      const creditsPerUse = Number(item.avg_credit_cost || 0);
      acc[item.operation_type] = creditsPerUse > 0 ? Math.floor(credits / creditsPerUse) : 0;
      return acc;
    }, {} as Record<string, number>);
}; 